// Lightweight client-side profanity filter shared by the user-side TalkToExperts
// chat and the expert-side ExpertDashboard chat. Keeps moderation behavior
// consistent across both apps without needing a server-side function.

// Core blocked terms (lowercase, base forms). Extended with English/Hindi profanity 
// and inappropriate romantic terms.
const BLOCKED_WORDS = [
  "fuck", "fucking", "fucker", "motherfucker", "shit", "bullshit", "bitch",
  "asshole", "ass", "bastard", "cunt", "dick", "pussy", "slut", "whore",
  "fag", "faggot", "nigger", "nigga", "retard", "rape", "molest",
  "chutiya", "madarchod", "behenchod", "bhenchod", "randi", "saala",
  "harami", "kutta", "kutiya", "gandu", "lund", "chod", "bhosdi",
  "bhosadi", "lavda", "suck my", "fuck you", "fuck off", "go to hell",

  "i love you", "i love u", "i like you", "i like u", "love you", "love u",
  "babu", "shona", "jaan", "jaanu", "darling", "sexy", "hotty",
  "kamina", "saali", "kamini", "chut", "gaand", "marwa", "tatte"
];

// Regex targeting standard emojis, symbols, pictographs, and transport indicators
const EMOJI_REGEX = /[\u{1F300}-\u{1F9FF}]|[\u{2700}-\u{27BF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{2600}-\u{26FF}]|[\u{1F1E6}-\u{1F1FF}]/u;

// Normalizes common leetspeak / spacing tricks before matching
function normalize(text) {
  return text
    .toLowerCase()
    .replace(/[@4]/g, "a")
    .replace(/[3€]/g, "e")
    .replace(/[1!|]/g, "i")
    .replace(/[0]/g, "o")
    .replace(/[5$]/g, "s")
    .replace(/[7]/g, "t")
    .replace(/[*]/g, "u")     
    .replace(/[^a-z\s]/g, "") // strip remaining punctuation/symbols
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Checks a message for profanity, inappropriate romantic phrases, or emojis.
 * @param {string} text - raw message text before sending
 * @returns {{ blocked: boolean, matchedWord: string|null, reason: string|null }}
 */
export function checkProfanity(text) {
  if (!text || !text.trim()) return { blocked: false, matchedWord: null, reason: null };

  // 1. Emoji check on the raw text
  if (EMOJI_REGEX.test(text)) {
    return { blocked: true, matchedWord: "Emoji", reason: "emoji" };
  }

  const normalized = normalize(text);
  const words = normalized.split(" ");

  for (const word of BLOCKED_WORDS) {
    const normWord = normalize(word);

    if (normWord.includes(" ")) {
      // Multi-word phrase (e.g. "i love you") — check as substring of the full normalized text
      if (normalized.includes(normWord)) {
        return { blocked: true, matchedWord: word, reason: "profanity" };
      }
      continue;
    }

    // Single word match
    if (words.includes(normWord)) {
      return { blocked: true, matchedWord: word, reason: "profanity" };
    }

    // Catch spaced-out evasion ("f u c k")
    const collapsedSingleLetters = normalized.replace(/\b(\w)(?:\s+(\w)\b)+/g, (m) => m.replace(/\s/g, ""));
    if (collapsedSingleLetters.split(" ").includes(normWord)) {
      return { blocked: true, matchedWord: word, reason: "profanity" };
    }
  }

  return { blocked: false, matchedWord: null, reason: null };
}

export default checkProfanity;